// BrandWheelLoader — the iYuga Wheel AS the loading state (THE ONE unified loader).
// WL (Fable Hail Mary, wave 5) · ARC-SWEEP-SEQUENTIAL (ISP-9999, bug #908,
// 2026-08-21, Gordon's PRECISE spec, supersedes every prior #908 attempt).
// The product IS a Wheel, so the wait is the Wheel visibly GENERATING itself,
// segment by segment — not a generic spinner, not an opacity fade/plop. Use it
// wherever a spinner/skeleton means "we're fetching", on EVERY surface.
//
// #908 (Gordon, REPEAT COMPLAINT, now FINAL): every prior pass — a whole-mark
// opacity fade between two randomized wheels, then a per-segment opacity
// "cascade" (`bwl-seg`) — was still an OPACITY plop: segments (or the whole
// mark) merely toggling visible/invisible, never actually drawing. That is not
// the real Wheel's own animation. THE TARGET is the D3-Wheel GENERATION tween
// (src/components/D3Wheels/createPie.js's updateArcs(): `.attrTween("d", ...)`
// interpolates a wedge's endAngle from its startAngle up to its own endAngle, so
// the arc visibly GROWS CLOCKWISE within its own angular span). This component
// reproduces that exact growth (arcSweepEngine.js, sharing brandWheel.js's
// `wedgePathD` geometry so the loader can never draw a different-shaped wedge
// than the real Wheel) — SEQUENTIALLY: segment 1 sweeps in, THEN segment 2
// starts, THEN segment 3, staggered all the way around the ring (never
// simultaneous — see arcSweepEngine.js's `segmentDelay`). Once every segment has
// swept in, the complete wheel holds briefly, then clears and a freshly
// randomized wheel (`randomBrandWheel`) sweeps in the same way — looping.
//
// ON BRAND (G-19): the mark is composed from the canonical brand Wheel renderer
// (brandWheelSVG / brand-wheel via `brandWheelInner`) — NOT a hand-rolled ring.
// Each generation is still unequal jewel segments + a calm white Portrait disc,
// drawn from the brand palette/span-generation rules. Never re-draw a Wheel here.
//
// START EMPTY, THEN SWEEP IN (Gordon 2026-08-23, supersedes the old two-phase
// "complete wheel fades in first" spec — verbatim: "Loader should START EMPTY
// instead of starting with a full wheel"):
//   The FIRST painted frame is an EMPTY ring (zero segments drawn), and the
//   sequential arc-sweep generation loop runs from mount, so the wheel builds up
//   one segment at a time from empty -> full -> (peak) -> degenerates one at a
//   time -> empty -> a freshly randomized wheel sweeps in the same way, looping.
//   The whole-mark PHASE 1 opacity fade (`data-phase="entry"`, ENTRY_MS = 200ms)
//   still plays as a gentle container fade, but it now fades in an already-empty,
//   already-sweeping mark rather than a pre-filled complete Wheel — the "full
//   wheel flashed at the very start" is gone.
//   WHY (reversed): Gordon found the complete-wheel flash at t=0 wrong; the product
//   IS a Wheel being generated, so the wait must START from nothing and build. (The
//   old rationale — fast loads showing a half-formed wheel — is retired by owner
//   decision; a fast load now simply shows the opening of the generate sweep.)
//   `skipEntry` (handoff takeover) still starts from the complete Wheel so the seam
//   has no blank dip (#908 defect 2).
//   prefers-reduced-motion => the complete Wheel, static, no entry, no sweep.
//
// MOTION — THE ARC-SWEEP (see arcSweepEngine.js): each `<path>`'s `d` attribute
// is regenerated every animation frame (imperative DOM writes via a ref, exactly
// how D3 drives its own transitions — no React re-render per frame) so the
// wedge's arc grows from nothing to its full angular span; no transform, no
// rotation/spin (rim animation is still banned), no opacity ever touched by this
// mechanism. `index.jsx` owns the requestAnimationFrame loop and decides when a
// cycle is done (arcSweepEngine's `computeFrame`) and swaps in a freshly
// randomized wheel. Off-screen the loop is cancelled outright (IntersectionObserver)
// — no wasted per-frame work on a loader the user can't see, and it resumes from
// exactly where it left off. Under prefers-reduced-motion it renders a still,
// COMPLETE Wheel at rest (the sweep loop never starts) and still announces its
// busy state.
//
// A11Y: role=status + aria-live="polite" + aria-busy="true" with an accessible
// name (the `label`). The SVG is aria-hidden; the name is read from a
// visually-hidden span. No focusable elements → never traps focus.
//
// Variants (accessible name defaults per context, override with `label`):
//   <BrandWheelLoader variant="inline" />   // 20px, FIXED — inside buttons/fields
//   <BrandWheelLoader variant="medium" />   // cards/panels/overlays
//   <BrandWheelLoader variant="page" />     // route-level, block-centred in container
//   <BrandWheelLoader variant="hero" />     // full-page first load, block-centred
//   <BrandWheelLoader variant="fullpage" /> // VIEWPORT-centred overlay (both axes),
//                                           //   the full-page loader (always screen-centre)
//   <BrandWheelLoader size={40} label="Loading your Wheels…" />
//
// SIZE (fix-gordon-0929-P1-wheel-loader-scales-with-space): every variant above
// except `inline` is CONTAINER-RELATIVE — it measures its immediate parent (the
// box the caller already sizes to "the space it fills") and clamps to
// clamp(48px, 12% of the container's short side, 160px) — see responsiveSize.js.
// The 56/96/160 numbers that used to be each variant's LITERAL size are now only
// the fallback for an unmeasured (0-size) parent. `inline` and an explicit `size`
// prop stay fixed, never measured — a 20px mark inside a button is not "filling"
// anything and must not balloon to the 48px floor.
//
// FILL / TONE (QA-104, Gordon design directive 2026-08-04 — see
// design-handoff/DESIGN-DECISIONS-LOG.md D-22):
//   <BrandWheelLoader fill="filled" />   // DEFAULT — the on-brand jewel palette +
//                                        //   white Portrait disc (brandWheelInner({})).
//   <BrandWheelLoader fill="outline" />  // stroke-only, thin — same geometry, fill:none.
//                                        //   Equivalent to the legacy `lineArt` boolean.
//   <BrandWheelLoader tone="light" />    // DEFAULT — for a light/white surface.
//   <BrandWheelLoader tone="dark" />     // for a dark/coloured surface — swaps the
//                                        //   outline ink white<->navy and the fullpage
//                                        //   scrim; the FILLED jewel palette is not
//                                        //   re-coloured (the white disc is the contrast
//                                        //   anchor on dark; no invented palette — the
//                                        //   off-brand-color gate + Nick's principles).
//   Back-compat: the legacy `lineArt` (boolean) and `onDark` (boolean) props still work
//   exactly as before — `fill`/`tone` are additive, not a breaking rename.
//
// LINE-ART (design-doc variant B — the AI-generation floating side-page indicator):
//   <BrandWheelLoader fill="outline" variant="inline" />  // stroke-only, thin
//   Auto-contrasts: tone="dark" => white line, else brand-navy ink (#123051). Same wheel
//   geometry as the filled mark, just stroked (fill:none). No white Portrait disc.
//
// CAPTION (optional): a quiet, on-brand VISIBLE line beneath the Wheel so a long
// wait reads as intentional, not empty. Pass `caption="…"` (or `caption` / `true`
// to reuse `label`). The visible caption is aria-hidden="true" — the busy state is
// announced ONCE via the visually-hidden `label`. DM Sans / ink #123051. Omit it and
// rendering is byte-for-byte the old behaviour (backward compatible).

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { brandWheelInner, randomBrandWheel, BRAND_WHEEL_SPANS } from "../BrandWheel/brandWheel";
import { holdStartMs, computeFrame } from "./arcSweepEngine";
import { pickNextLoaderPalette } from "./loaderPalettes";
import { computeContainerRelativeSize } from "./responsiveSize";
import "./brandWheelLoader.css";

const SIZES = { inline: 20, medium: 56, page: 96, hero: 160, fullpage: 160 };

// fix-gordon-0929-P1-wheel-loader-scales-with-space: every space-FILLING
// variant (the gallery main area, wheel-view/builder canvases, modals, cards)
// now sizes itself off the container it fills instead of the fixed SIZES
// value above (SIZES stays as the fallback for an unmeasured/0-size
// container, and as the fixed truth for `inline` — a 20px mark that lives
// INSIDE a button/field, never a space it "fills"). See responsiveSize.js.
const SCALES_WITH_CONTAINER = new Set(["medium", "page", "hero", "fullpage"]);

// Phase-1 entry duration. Short enough that even a very brief load completes it
// before the loop starts, long enough to read as a deliberate fade-in.
export const ENTRY_MS = 200;

// #937 follow-up (Gordon, 2026-08-22): the loader is LOCKED to a FIXED 6-segment
// wheel — the SEGMENT-COUNT randomization (the old 4-7 draw in randomBrandWheel)
// is removed so the cycle length no longer wobbles (was ~1.75-2.31s across 4-7;
// now a steady ~2.0s for a fixed 6 — see arcSweepEngine.js's SEGMENT_SWEEP_MS).
// PALETTE randomization STAYS (loaderPalettes.js, per-cycle random pick); only
// the segment COUNT is pinned. 6 is the canonical BRAND_WHEEL_SPANS shape.
export const LOADER_SEGMENT_COUNT = 6;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// competitor-0824-bwl-raf-leak: is `el` ACTUALLY rendered-visible right now?
// WHY: the arc-sweep loop below is paused off-screen by an IntersectionObserver,
// but IO is GEOMETRY-only — a loader kept MOUNTED and hidden via `visibility:hidden`
// / `opacity:0` still occupies a layout box, so it keeps "intersecting" and the
// observer never fires. Such a hidden loader would churn ~360 `d`-attribute writes
// per second forever on a mark nobody can see (measured on /discover, gallery,
// messages, /login). `checkVisibility()` catches exactly that class the observer
// misses — CSS `visibility`, `opacity:0`, `content-visibility`, and hidden
// ancestors. Environments without it (jsdom, SSR) return "visible" so tests and
// non-DOM renders are unaffected; the unit test drives the hidden branch by
// stubbing `checkVisibility` on the mounted node.
const isRenderedVisible = (el) => {
  if (!el) return false;
  if (typeof el.checkVisibility !== "function") return true;
  return el.checkVisibility({
    visibilityProperty: true,
    opacityProperty: true,
    contentVisibilityAuto: true,
  });
};

const BrandWheelLoader = ({
  variant = "medium",
  size,
  label = "Loading…",
  caption,
  pauseOffscreen = true,
  fill,
  tone,
  onDark = false,
  lineArt = false,
  stroke,
  strokeWidth = 4,
  // stgfix-1008-B49 (Kelly, 2026-10-07): the indeterminate arc-sweep loop
  // below is right for a sub-second page/skeleton wait, but a caller that
  // already has REAL determinate progress (e.g. video-upload `percent`) and
  // can run for many seconds would otherwise show the mark cycling back to
  // an empty ring — by design (#908 START EMPTY) — every ~2s, reading as the
  // operation stalling/restarting rather than progressing. `loop={false}`
  // renders the complete Wheel once and never touches it again, so there is
  // no cycle boundary left to go blank. Default true: every other call site
  // keeps the existing indeterminate loop unchanged.
  loop = true,
  // kelly-1003-P2-gallery-loading-white-circle-on-wallpaper: lets a `filled`
  // caller (e.g. GallerySkeleton) drop the white Portrait disc while keeping
  // the multicolour jewel wedges. Default true so every other call site
  // (which wants the disc as "the contrast anchor", per QA-104) is unchanged.
  disc = true,
  // `reroll` predates this component always randomizing (it used to gate an
  // opt-in re-roll loop). Randomizing is now the default loop for every variant
  // (#908 — Gordon reported it missing everywhere, not just where this was passed),
  // so the prop is accepted for back-compat but is a no-op.
  // eslint-disable-next-line no-unused-vars
  reroll,
  // #908 defect 2 (flash-to-blank handoff): a caller passes `skipEntry` when this
  // mount is taking over from ANOTHER BrandWheelLoader that was already on screen
  // an instant ago (route-chunk Suspense fallback -> a page's own data-loading
  // state, e.g. GallerySkeleton) — the two are separate component instances, so
  // without this the new one would restart PHASE 1's whole-mark opacity fade from
  // 0, which reads as a blank dip between the two loaders even though neither
  // ever truly unmounts to nothing. Skipping straight to "entered" renders the
  // complete Wheel at full opacity from the very first frame, same as reduced
  // motion does, so the handoff is visually continuous.
  skipEntry = false,
  className = "",
  style,
  ...rest
}) => {
  // QA-104: `fill`/`tone` are the current API; `lineArt`/`onDark` are legacy booleans
  // kept working for any call site that hasn't migrated. Explicit `fill`/`tone` wins
  // when given; otherwise fall back to the legacy prop so nothing changes behaviour.
  // `fill="mono"` (Gordon 2026-08-15, button/login loader spec): the brand-wheel
  // geometry rendered as MONOCHROMATIC FILLED wedges in ONE ink (white on dark, navy
  // on light) with NO Portrait disc — only the randomized-size wedges show, nothing
  // else. Distinct from "filled" (multicolour jewels + white disc) and "outline"
  // (stroked, no fill). This is the correct on-button loader.
  const isMono = fill === "mono";
  const isOutline = fill ? fill === "outline" : lineArt;
  const isDark = tone ? tone === "dark" : onDark;

  const rootRef = useRef(null);
  const svgRef = useRef(null);
  const [paused, setPaused] = useState(false);
  // fix-gordon-0929-P1-wheel-loader-scales-with-space: measured off the
  // immediate parent (the box the caller already sizes to "the space it
  // fills" — Loader.jsx/GallerySkeleton.jsx etc. give it width/height:100%),
  // so this is the ONE place every call site's loader becomes container-
  // relative, with zero per-page changes. `null` until measured, or on a
  // fixed-size `inline` mark / an explicit `size` override, which never
  // measure at all.
  const scalesWithContainer = SCALES_WITH_CONTAINER.has(variant) && size == null;
  const [containerPx, setContainerPx] = useState(null);
  useLayoutEffect(() => {
    if (!scalesWithContainer) return undefined;
    const parent = rootRef.current && rootRef.current.parentElement;
    if (!parent) return undefined;
    const measure = (w, h) => setContainerPx(computeContainerRelativeSize({ width: w, height: h }));
    measure(parent.clientWidth, parent.clientHeight);
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver((entries) => {
      const rect = entries[0] && entries[0].contentRect;
      measure(rect ? rect.width : parent.clientWidth, rect ? rect.height : parent.clientHeight);
    });
    ro.observe(parent);
    return () => ro.disconnect();
  }, [scalesWithContainer]);
  const px = size || (scalesWithContainer && containerPx) || SIZES[variant] || SIZES.medium;
  // Reduced motion is read once per mount: it decides whether there is any motion at
  // all, and a mid-load media-query flip is not worth a re-render.
  const [reduce] = useState(prefersReducedMotion);
  // PHASE 1 -> PHASE 2. Reduced motion goes straight to "entered" and simply never
  // starts the arc-sweep loop below, so the mark is a complete, still Wheel from
  // frame one. `skipEntry` does the same for a handoff mount (#908 defect 2).
  const [entered, setEntered] = useState(reduce || skipEntry);
  // `null` => render the canonical fixed wheel (brandWheelInner({}) defaults) —
  // the whole, recognisable Wheel every mount starts on and PHASE 1 always shows.
  // Once PHASE 2 starts, this holds the current randomized {spans, colors} draw.
  const [wheel, setWheel] = useState(null);
  // Elapsed ms into the CURRENT arc-sweep generation cycle — a ref (not state) so
  // pausing/resuming (off-screen) can freeze and resume it without re-rendering,
  // and so it survives across the rAF loop's own effect re-runs.
  const elapsedRef = useRef(0);
  // True only until the very first time the sweep loop starts. Gordon 2026-08-23:
  // the loader STARTS EMPTY and sweeps in one segment at a time, so that first run
  // seeds `elapsedRef` at 0 (an empty ring) and generates from there, exactly like
  // every subsequent cycle — never a pre-filled full wheel flashed at frame one.
  // The ONE exception is `skipEntry` (a handoff mount taking over from another
  // on-screen loader): it still seeds at the START of the hold (every segment
  // already swept) so the takeover shows the complete Wheel from frame one and the
  // seam has no blank dip (#908 defect 2) — an empty flash there would be the very
  // regression skipEntry exists to prevent.
  const firstCycleRef = useRef(true);
  // `caption === true` (or the bare `caption` prop) reuses the accessible label as the
  // visible line; a string sets its own text. Anything falsy => no visible caption.
  const captionText = caption === true ? label : caption;

  // Pause the animation while off-screen — no wasted GPU/CPU on a loader the user
  // can't see (long lists, tab-away). Cheap, and reversible on re-entry.
  useEffect(() => {
    if (!pauseOffscreen) return undefined;
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry) setPaused(!entry.isIntersecting);
      },
      { threshold: 0 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [pauseOffscreen]);

  // PHASE 1 (all variants): the whole-mark opacity fade (`data-phase="entry"`) runs
  // for ENTRY_MS, then flips to "loop". This only drives the CSS container fade now;
  // the arc-sweep loop below runs from mount regardless (Gordon 2026-08-23 — start
  // empty and sweep in immediately), so this no longer holds a complete Wheel.
  useEffect(() => {
    if (entered) return undefined;
    const t = setTimeout(() => setEntered(true), ENTRY_MS);
    return () => clearTimeout(t);
  }, [entered]);

  // PHASE 2: THE ARC-SWEEP GENERATION LOOP (#908, arc-sweep-sequential spec) —
  // reproduces the real Wheel's D3 arcTween (createPie.js's updateArcs) per
  // segment, staggered SEQUENTIALLY (arcSweepEngine.js's `segmentDelay`: segment 1
  // sweeps in, THEN segment 2, THEN segment 3 — never simultaneous). Every
  // requestAnimationFrame this writes each `<path>`'s `d` attribute directly via
  // `svgRef` (imperative, D3-style — no React re-render per frame); only the
  // CYCLE boundary (every segment swept + held, `computeFrame`'s `done`) goes
  // through React state, to swap in a freshly randomized {spans, colors} wheel.
  // `wheel` is a dependency so that swap naturally restarts this effect for the
  // new cycle. Suspended (cancelled, not just skipped) while reduced-motion or
  // off-screen; `elapsedRef` persists across a pause so resuming continues the
  // sweep exactly where it left off rather than jumping. It is NOT gated on
  // `entered` (Gordon 2026-08-23): the sweep runs from mount so the FIRST painted
  // frame is the empty ring at elapsed 0 (see the synchronous `applyFrame` below),
  // then builds up — the PHASE 1 whole-mark opacity fade (`data-phase`) simply
  // plays over the top of that already-empty, already-sweeping mark.
  useLayoutEffect(() => {
    // stgfix-1008-B49: `loop={false}` opts out of the sweep entirely — the
    // declarative complete-wheel markup below (`inner`) is left untouched, so
    // there is no empty-ring frame and no cycle boundary to go blank at.
    if (reduce || paused || !loop) return undefined;
    const spans = (wheel && wheel.spans) || BRAND_WHEEL_SPANS;

    if (firstCycleRef.current) {
      // START EMPTY (elapsed 0) and sweep in — except a `skipEntry` handoff, which
      // seeds at the hold so the takeover shows the complete Wheel with no blank dip.
      elapsedRef.current = skipEntry ? holdStartMs(spans.length) : 0;
      firstCycleRef.current = false;
    }

    const applyFrame = (elapsedMs) => {
      elapsedRef.current = elapsedMs;
      const { ds, done } = computeFrame(spans, elapsedMs);
      const paths = svgRef.current ? svgRef.current.querySelectorAll("path") : [];
      ds.forEach((d, i) => {
        if (paths[i]) paths[i].setAttribute("d", d);
      });
      return done;
    };

    // Paint the current elapsed state synchronously, BEFORE the browser paints
    // (useLayoutEffect), so a freshly swapped wheel — rendered fully-drawn by
    // brandWheelInner — never flashes complete before its first sweep frame.
    applyFrame(elapsedRef.current);

    // The sweep now runs from mount (start-empty), so it can be reached in
    // environments where `requestAnimationFrame` is unavailable (jsdom without the
    // stub, SSR). Guard it: if it's missing we've already painted the synchronous
    // first frame (the empty ring) above — just don't schedule the animation, rather
    // than throwing. Mirrors the existing cancelAnimationFrame guard below.
    let raf;
    let cycleStart = null;
    const schedule = (cb) =>
      typeof window.requestAnimationFrame === "function"
        ? window.requestAnimationFrame(cb)
        : undefined;
    const tick = (now) => {
      // competitor-0824-bwl-raf-leak: a parent can flip the loader to
      // `visibility:hidden` / `opacity:0` MID-RUN while keeping it mounted — a state
      // the IntersectionObserver above never sees (a hidden box still intersects the
      // viewport). Re-check every frame and STOP the loop dead when the mark is not
      // actually rendered-visible: no `computeFrame`, no `d` write, no reschedule =>
      // ZERO per-frame work on a loader nobody can see. It re-arms through the
      // IntersectionObserver on the next intersection change, or on remount.
      if (!isRenderedVisible(rootRef.current)) return;
      if (cycleStart === null) cycleStart = now - elapsedRef.current;
      const done = applyFrame(now - cycleStart);
      if (done) {
        elapsedRef.current = 0;
        // #937: each generation cycle autoselects one of the curated multi-colour
        // themes at random (see loaderPalettes.js). QA-1280 (Gordon 2026-08-25):
        // pickNextLoaderPalette EXCLUDES the immediately-previous theme, so the
        // loader never shows the same palette twice in a row. Only the FILLED brand
        // loader consumes wheel.colors; mono/outline variants override colours and
        // are unaffected. #937 follow-up: the SEGMENT COUNT is pinned to
        // LOADER_SEGMENT_COUNT (min===max===6) so every generation is a fixed
        // 6-segment wheel — only the spans/palette vary, not the count.
        setWheel(
          randomBrandWheel({
            palette: pickNextLoaderPalette(),
            minSegments: LOADER_SEGMENT_COUNT,
            maxSegments: LOADER_SEGMENT_COUNT,
          })
        );
        return;
      }
      raf = schedule(tick);
    };
    // Don't even schedule frame 1 for a loader that is mounted but hidden
    // (visibility:hidden / opacity:0) — the synchronous first paint above already
    // seeded the current state; scheduling would just start the churn the
    // IntersectionObserver can't stop. (competitor-0824-bwl-raf-leak)
    raf = isRenderedVisible(rootRef.current) ? schedule(tick) : undefined;
    return () => {
      // jsdom (test environment) implements requestAnimationFrame but not always
      // cancelAnimationFrame — guard so cleanup never throws there.
      if (raf && typeof window.cancelAnimationFrame === "function") {
        window.cancelAnimationFrame(raf);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduce, paused, wheel, skipEntry, loop]);

  // Composed from the canonical brand Wheel generator every time (G-19) — `wheel`
  // is null until the first sweep cycle completes, so PHASE 1 and the opening
  // moments of PHASE 2 render the same fixed canonical mark; `randomBrandWheel()`
  // output (a fixed 6-segment count since #937 — only the spans/colors vary)
  // drives every generation after
  // that (the sweep loop above only ever mutates each `<path>`'s `d`, never this
  // markup, between swaps). Outline
  // (`fill="outline"`) strokes the SAME geometry with an auto-contrasted ink (white
  // on dark, brand-navy #123051 on light) and no disc; mono is a single ink, no
  // disc — for both, only the segment SIZES randomize, not the (single) color.
  const inkStroke = stroke || (isDark ? "#FFFFFF" : "#123051");
  const wheelOpts = wheel || {};
  const inner = isMono
    ? // MONO: filled wedges, ALL one ink, NO disc/portrait — the randomized-size
      // segments only. white on dark, navy #123051 on light.
      brandWheelInner({ ...wheelOpts, colors: [inkStroke], disc: false, portrait: "" })
    : isOutline
    ? brandWheelInner({ ...wheelOpts, lineArt: true, stroke: inkStroke, strokeWidth })
    : brandWheelInner({ ...wheelOpts, disc });

  // `page`/`hero` block-centre in their container; `fullpage` viewport-centres
  // (fixed overlay, both axes) — the always-screen-centre full-page loader.
  const wrapClass =
    variant === "fullpage"
      ? "bwl-fullpage"
      : variant === "page" || variant === "hero"
      ? "bwl-page"
      : "bwl-root";
  const classes = [
    "bwl-wrap",
    wrapClass,
    // bwl-lineart kept for back-compat (existing tests/CSS hooks target it);
    // bwl-outline/bwl-filled are the QA-104 explicit-API class names.
    isMono ? "bwl-mono" : isOutline ? "bwl-lineart bwl-outline" : "bwl-filled",
    captionText ? "bwl-has-caption" : "",
    paused ? "bwl-paused" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      ref={rootRef}
      role="status"
      aria-live="polite"
      aria-busy="true"
      className={classes}
      style={style}
      data-on-dark={isDark ? "true" : undefined}
      {...rest}
    >
      <svg
        ref={svgRef}
        aria-hidden="true"
        className="bwl-svg"
        width={px}
        height={px}
        viewBox="0 0 120 120"
        data-phase={entered ? "loop" : "entry"}
        dangerouslySetInnerHTML={{ __html: inner }}
      />
      {captionText ? (
        <div className="bwl-caption" aria-hidden="true">
          {captionText}
        </div>
      ) : null}
      <span className="bwl-visually-hidden">{label}</span>
    </div>
  );
};

export default BrandWheelLoader;
